import { useCallback, useEffect, useState } from "react";
import {
  applyMagicPublish,
  magicPublishPlan,
  type MagicAssets,
  type MagicPublishPlan,
  type MagicScenePlan,
} from "@greg-chick/shared";
import { adminApi, contentBase, loadBookDoc, loadManifest, publishAssets, type ManifestEntry } from "../lib/contentApi";
import { Msg } from "./ui";

/**
 * 🪄 **저장소에 있는 마법을 아이 화면에 올리기** — 운영자가 **제 열쇠로** 누른다.
 *
 * ## 🔴 왜 단추가 필요한가 (2026-09-09)
 * 마법 장면을 만들어 배포해도 아이 화면에는 안 나온다. 아이 화면이 읽는 목록은 「정적 폴백 위에
 * 게시본을 덮은 것」이고, 덮기는 **항목 통째로** 게시본이 이긴다 — 그 쪽 타깃은 `.mind` 때문에
 * 이미 게시돼 있으니 저장소가 얹어 둔 정의가 통째로 가려진다.
 *
 * 지금까지 이걸 푸는 길은 **명령줄 하나뿐**이었다(`ADMIN_TOKEN=… pnpm magic:seed -- --write`).
 * 그건 운영 열쇠를 손에 들고 터미널을 여는 일이라 **개발자만** 할 수 있었고, 그래서 만들어 둔
 * 마법 다섯 장면이 며칠째 아이에게 안 닿았다. 운영자는 이미 관리자에 열쇠를 넣고 들어와 있다 —
 * **여기서 누르면 된다.**
 *
 * ## 🔴 판정은 shared 한 곳
 * 무엇을 올리고 무엇을 그대로 둘지는 `magicPublishPlan` 이 정한다 — `pnpm magic:seed` 와 **같은 규칙**.
 * 여기서 다시 짜면 「명령으로는 되는데 단추로는 안 된다」가 된다.
 *
 * ## 🔴 덮어쓰기는 한 장면씩, 손으로
 * 게시본에 이미 있는 장면은 자동으로 안 바꾼다(관리자에서 고친 것일 수 있다). 저장소 것과
 * 다르면 **그 장면만** 골라 바꾸게 한다 — 그 길이 없으면 저장소에서 고친 자리가 영영 안 닿는다.
 */
export function MagicPublishCard() {
  const [repo, setRepo] = useState<MagicAssets | null>(null);
  const [live, setLive] = useState<MagicAssets | null>(null);
  /** 쪽 이름 — 사람이 찾아갈 수 있는 말로(「p11」이 아니라 「12쪽」). */
  const [where, setWhere] = useState<Record<string, string>>({});
  const [force, setForce] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setFailed(false);
    const get = async (url: string): Promise<MagicAssets | null> => {
      try {
        const r = await fetch(url, { cache: "no-store" });
        if (!r.ok) return null;
        const m = (await r.json()) as { assets?: MagicAssets };
        return m?.assets ?? null;
      } catch {
        return null;
      }
    };
    const [rp, lv] = await Promise.all([
      get(`${contentBase()}/content/asset-manifest.json`),
      get(`${adminApi()}/api/asset-manifest`),
    ]);
    // 🔴 「없다」와 「못 물어봤다」는 다른 말이다 — 못 읽었으면 **아무 말도 안 한다**(빈 계획을 보이면
    //    「올릴 것이 없다」로 읽히고, 운영자는 다 됐다고 생각하고 나간다)
    if (!rp || !lv) {
      setFailed(true);
      return;
    }
    setRepo(rp);
    setLive(lv);
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const plan: MagicPublishPlan | null = repo && live ? magicPublishPlan(repo, live, force) : null;

  /** 장면이 어느 책 몇 쪽인지 — 책 문서에서 센다(쪽 id 와 쪽 번호는 한 칸 어긋난다). */
  useEffect(() => {
    if (!plan) return;
    const ids = [...plan.add, ...plan.differs].map((s) => s.sceneId);
    // 🔴 점이 있는 것만 책 쪽 — 경주 카드(`ar_sumaksae`)를 자르면 책 「ar_sumaks」를 불러오려 한다(2026-09-13)
    const books = new Set(ids.filter((id) => id.indexOf(".") > 0).map((id) => id.slice(0, id.indexOf("."))));
    let alive = true;
    for (const b of books) {
      void loadBookDoc(b).then((doc) => {
        if (!alive) return;
        const title = typeof doc.title_ko === "string" ? doc.title_ko : b;
        const next: Record<string, string> = {};
        doc.pages.forEach((p, i) => {
          const pid = String((p as { id?: unknown }).id ?? "");
          if (pid) next[`${b}.${pid}`] = `《${title}》 ${i + 1}쪽`;
        });
        setWhere((w) => ({ ...next, ...w }));
      });
    }
    return () => {
      alive = false;
    };
    // 장면 목록이 바뀔 때만 — plan 자체는 렌더마다 새 객체다
  }, [plan?.add.length, plan?.differs.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const name = (s: MagicScenePlan) => where[s.sceneId] ?? s.sceneId;

  async function publish() {
    if (!plan || !repo || plan.add.length === 0) return;
    setBusy(true);
    setMsg(null);
    try {
      // 🔴 게시 직전에 **다시 읽는다** — 그 사이 다른 곳이 올렸을 수 있다(게시는 통째로 바꾸기다)
      const base = await loadManifest();
      const fresh = magicPublishPlan(repo, live!, force);
      const next = applyMagicPublish(base.assets as MagicAssets, repo, fresh);
      const out = await publishAssets(next as Record<string, ManifestEntry>);
      setMsg(`✅ ${fresh.add.length}곳을 아이 화면에 올렸어요 (v${out.version}). 책을 열어 눌러 보세요.`);
      setForce([]);
      await load();
    } catch (e) {
      setMsg(`⚠️ 못 올렸어요: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  if (failed) {
    return (
      <div className="magicpub">
        <p className="hint">
          🪄 마법이 아이 화면에 올라갔는지 <b>못 물어봤어요</b>(서버 연결). 잠시 뒤 다시 확인해 주세요.
        </p>
        <button type="button" className="btn btn--sm" onClick={() => void load()}>
          다시 확인
        </button>
      </div>
    );
  }
  if (!plan) return null;
  if (plan.add.length === 0 && plan.differs.length === 0) {
    return <p className="hint">🪄 마법 장면은 모두 아이 화면에 올라가 있어요.</p>;
  }

  return (
    <div className="magicpub">
      <h3 className="magicpub__h">🪄 아직 아이 화면에 없는 마법</h3>
      <p className="hint">
        만들어는 뒀는데 <b>아이 화면에는 아직 안 나가는</b> 장면이에요. 눌러도 아무 일이 안 나요.
        여기서 올리면 바로 나갑니다.
      </p>

      {plan.add.length > 0 ? (
        <>
          <ul className="magicpub__list">
            {plan.add.map((s) => (
              <li key={s.sceneId}>
                <b>{name(s)}</b>
                <span className="hint">
                  {" "}
                  {s.camera ? " · 📷 카메라 인식 파일만 채워요(마법은 이미 그대로예요)" : ` · 층 ${s.layers}개 · 누르는 자리 ${s.hotspots}개`}
                  {!s.camera && s.imageKeys.length > 0 ? ` · 그림 ${s.imageKeys.length}개` : ""}
                </span>
              </li>
            ))}
          </ul>
          <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void publish()}>
            {busy ? "올리는 중…" : `아이 화면에 올리기 (${plan.add.length}곳)`}
          </button>
        </>
      ) : null}

      {plan.differs.length > 0 ? (
        <div className="magicpub__diff">
          {/* 🔴 「저장소」는 개발자 말이다 — 운영자가 층 옆 「저장소 그림으로 바꾸기」를 두고 「뭔지 모르겠다」고 했다
              (2026-09-11). 이 카드도 같은 말을 썼다. 운영자에게는 **무엇이 있는지**로 말한다: 새로 다듬은 판. */}
          <h4 className="magicpub__h4">새로 다듬은 판이 있는 장면</h4>
          <p className="hint">
            아이 화면에도 있지만 <b>새로 다듬은 판과 달라요</b>. 관리자에서 직접 고치신 것일 수
            있어 <b>자동으로 안 바꿉니다</b> — 새 판으로 바꾸려면 골라 주세요.
          </p>
          {/* 🔴 **한 번에 고르는 길** — 저장소에서 여러 장면을 한꺼번에 고치면(소리를 전부 붙인다든지)
              여기 목록이 통째로 뜬다. 일곱 번 누르게 하면 운영자는 그냥 안 한다. */}
          {plan.differs.length > 1 ? (
            <button
              type="button"
              className="btn btn--sm"
              disabled={busy}
              onClick={() => setForce(plan.differs.map((d) => d.sceneId))}
            >
              전부 새 판으로 ({plan.differs.length}곳)
            </button>
          ) : null}
          <ul className="magicpub__list">
            {plan.differs.map((s) => (
              <li key={s.sceneId}>
                <b>{name(s)}</b>
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={busy}
                  onClick={() => setForce((f) => [...f, s.sceneId])}
                >
                  새 판으로 바꾸기
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {force.length > 0 ? (
        <p className="hint">
          ↩️ <b>{force.length}곳</b>을 새 판으로 바꾸도록 골랐어요. 위 [아이 화면에 올리기]를 눌러야
          실제로 바뀝니다.{" "}
          <button type="button" className="btn btn--sm" onClick={() => setForce([])}>
            고른 것 취소
          </button>
        </p>
      ) : null}

      {plan.keep.length > 0 ? (
        <details className="magicpub__keep">
          <summary className="hint">안 건드리는 것 {plan.keep.length}개</summary>
          <ul className="magicpub__list">
            {plan.keep.map((k) => (
              <li key={`${k.sceneId}:${k.why}`}>
                <b>{where[k.sceneId] ?? k.sceneId}</b> <span className="hint">— {k.why}</span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      <Msg text={msg} />
    </div>
  );
}
